/**
 * Восемь сфер «колеса баланса» — базовая система координат всего приложения.
 *
 * К сфере привязываются цели, задачи и привычки. Это даёт срез «на что реально
 * уходит моя жизнь» вместо плоского списка дел: круговая диаграмма мгновенно
 * показывает перекос — например, 70% задач в карьере и ноль в семье.
 *
 * Восемь встроенных сфер — не предел: человек заводит свои («Учёба сына»,
 * «Дача»), и они хранятся в базе. Здесь только встроенные — те, что нужны до
 * всякой базы: в демо-данных, в цветах режима дня, в значениях по умолчанию.
 * Полный список, встроенные плюс свои, собирает каталог сфер
 * (`entities/sphere/model/catalog.ts`).
 */

/** Стабильные ключи встроенных сфер. НЕ переименовывать — они хранятся в базе. */
export const SPHERE_IDS = [
  'health',
  'career',
  'finance',
  'social',
  'family',
  'growth',
  'joy',
  'spirit',
] as const;

/** Ключ встроенной сферы — тот, по которому есть запись в `SPHERES`. */
export type BuiltInSphereId = (typeof SPHERE_IDS)[number];

/**
 * Ключ сферы: встроенной или созданной человеком.
 *
 * Обычная строка, а не перечисление: своих сфер приложение заранее не знает.
 * Поэтому и подписи с цветом берутся из каталога, а не из статической таблицы.
 */
export type SphereId = string;

export interface SphereMeta {
  id: BuiltInSphereId;
  /** Название для интерфейса */
  title: string;
  /** Короткая подпись — для диаграммы и чипов, где мало места */
  short: string;
  /** Поясняет, что относить к сфере: снимает неопределённость при выборе */
  hint: string;
  /** CSS-переменная цвета (см. src/app/styles/theme.css) */
  cssVar: string;
  /**
   * Класс подписи поверх заливки цветом сферы.
   *
   * Почти везде это белый, но жёлтый слишком светлый — белым по нему не
   * прочитать. Хранить решение рядом с цветом надёжнее, чем вспоминать о нём
   * в каждом компоненте, который красит фон акцентом.
   */
  onAccentClass: string;
  /** Tailwind-класс текста — для случаев, когда inline-стиль неудобен */
  textClass: string;
  emoji: string;
}

export const SPHERES: Record<BuiltInSphereId, SphereMeta> = {
  health: {
    id: 'health',
    title: 'Здоровье и спорт',
    short: 'Здоровье',
    hint: 'Сон, питание, тренировки, медицина, энергия',
    cssVar: '--sphere-health',
    onAccentClass: 'text-white',
    textClass: 'text-sphere-health',
    emoji: '💚',
  },
  career: {
    id: 'career',
    title: 'Карьера и бизнес',
    short: 'Карьера',
    hint: 'Работа, проекты, профессиональные результаты',
    cssVar: '--sphere-career',
    onAccentClass: 'text-white',
    textClass: 'text-sphere-career',
    emoji: '🎯',
  },
  finance: {
    id: 'finance',
    title: 'Финансы',
    short: 'Финансы',
    hint: 'Доход, накопления, инвестиции, финансовая подушка',
    cssVar: '--sphere-finance',
    onAccentClass: 'text-white',
    textClass: 'text-sphere-finance',
    emoji: '💰',
  },
  social: {
    id: 'social',
    title: 'Окружение и друзья',
    short: 'Окружение',
    hint: 'Друзья, нетворкинг, сообщества, общение',
    cssVar: '--sphere-social',
    onAccentClass: 'text-white',
    textClass: 'text-sphere-social',
    emoji: '🤝',
  },
  family: {
    id: 'family',
    title: 'Семья и отношения',
    short: 'Семья',
    hint: 'Партнёр, дети, родители, близкие',
    cssVar: '--sphere-family',
    onAccentClass: 'text-white',
    textClass: 'text-sphere-family',
    emoji: '❤️',
  },
  growth: {
    id: 'growth',
    title: 'Личностный рост и развитие',
    short: 'Развитие',
    hint: 'Обучение, книги, навыки, саморазвитие',
    cssVar: '--sphere-growth',
    onAccentClass: 'text-white',
    textClass: 'text-sphere-growth',
    emoji: '📚',
  },
  joy: {
    // Ключ `joy` остаётся прежним: он записан в базе у существующих целей,
    // задач и привычек. Переименование затрагивает только подписи.
    id: 'joy',
    title: 'Отдых',
    short: 'Отдых',
    hint: 'Хобби, путешествия, впечатления, восстановление',
    cssVar: '--sphere-joy',
    // Жёлтый — единственный акцент, по которому белая подпись не читается.
    onAccentClass: 'text-[oklch(0.28_0.05_100)]',
    textClass: 'text-sphere-joy',
    emoji: '✨',
  },
  spirit: {
    id: 'spirit',
    title: 'Духовность и внутренний мир',
    short: 'Духовность',
    hint: 'Ценности, смыслы, медитация, внутренний баланс',
    cssVar: '--sphere-spirit',
    onAccentClass: 'text-white',
    textClass: 'text-sphere-spirit',
    emoji: '🕊️',
  },
};

/** Список в порядке отображения (колесо, фильтры, легенда диаграммы). */
export const SPHERE_LIST: SphereMeta[] = SPHERE_IDS.map((id) => SPHERES[id]);

/** Цвет встроенной сферы как CSS-значение — для inline-стилей и canvas/SVG. */
export function sphereColor(id: BuiltInSphereId): string {
  return `var(${SPHERES[id].cssVar})`;
}

export function isSphereId(value: unknown): value is BuiltInSphereId {
  return typeof value === 'string' && (SPHERE_IDS as readonly string[]).includes(value);
}
